import React, { Component, ErrorInfo, ReactNode } from 'react';
import tracker from '../utils/tracker';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to our tracking system
    tracker(
      'error_boundary_triggered',
      this.props.context || 'unknown_component',
      error.message,
      `${error.name}: ${error.message}\n${errorInfo.componentStack}`
    );

    this.setState({
      error,
      errorInfo
    });

    // Log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    tracker('error_boundary_retry', this.props.context || 'unknown_component', 'user_clicked_retry', '');
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{
          padding: '2rem',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          background: '#fef2f2',
          margin: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ color: '#dc2626', marginBottom: '1rem' }}>
            <h3>⚠️ Something went wrong</h3>
            <p>An error occurred in the {this.props.context || 'application'}.</p>
          </div>
          
          <details style={{ 
            margin: '1rem 0', 
            textAlign: 'left', 
            background: '#ffffff', 
            padding: '1rem', 
            borderRadius: '4px',
            border: '1px solid #e5e7eb'
          }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Error Details (for debugging)
            </summary>
            <div style={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
              <p><strong>Error:</strong> {this.state.error?.message}</p>
              <p><strong>Component Stack:</strong></p>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                background: '#f3f4f6', 
                padding: '0.5rem', 
                borderRadius: '4px',
                overflow: 'auto'
              }}>
                {this.state.errorInfo?.componentStack}
              </pre>
            </div>
          </details>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={this.handleRetry}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              🔄 Try Again
            </button>
            
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              🔃 Reload Page
            </button>
          </div>

          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#f0f9ff',
            border: '1px solid #0ea5e9',
            borderRadius: '6px'
          }}>
            <p style={{ color: '#0369a1', fontSize: '0.875rem', margin: 0 }}>
              💡 If this error persists, please try refreshing the page or contact support. 
              Your data is safe and no changes have been lost.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;